import { storageService } from './storageService';
import type { ChatMessage } from '../domain/chatTypes';
import { pruneChatHistory } from '../domain/chatHistory';

export const THREAD_CONVERSATIONS_KEY = 'zunelo_chat_conversations_v1';
export const RADAR_CHAT_KEY = 'zunelo_radar_chat_v1';
export const CHAT_MIGRATION_KEY = 'zunelo_chat_migration_v1';
export const MAX_THREAD_CONVERSATIONS = 50;

type ThreadConversationMap = Record<string, ChatMessage[]>;

function isConversationMap(value: unknown): value is ThreadConversationMap {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function capConversationKeys(map: ThreadConversationMap): ThreadConversationMap {
  const keys = Object.keys(map);
  if (keys.length <= MAX_THREAD_CONVERSATIONS) return map;
  const next: ThreadConversationMap = {};
  for (const key of keys.slice(-MAX_THREAD_CONVERSATIONS)) {
    next[key] = map[key];
  }
  return next;
}

export function getThreadChatHistory(itemId: string): ChatMessage[] {
  const conversations = storageService.get<unknown>(THREAD_CONVERSATIONS_KEY, {});
  if (!isConversationMap(conversations)) {
    return [];
  }
  const history = conversations[itemId];
  return Array.isArray(history) ? history : [];
}

export function saveThreadChatHistory(itemId: string, messages: ChatMessage[]): void {
  const stored = storageService.get<unknown>(THREAD_CONVERSATIONS_KEY, {});
  const conversations = isConversationMap(stored) ? stored : {};
  const next: ThreadConversationMap = {};
  for (const key of Object.keys(conversations)) {
    if (key !== itemId) next[key] = conversations[key];
  }
  next[itemId] = pruneChatHistory(messages);
  storageService.set(THREAD_CONVERSATIONS_KEY, capConversationKeys(next));
}

export function getAllThreadChatHistories(): Array<{ itemId: string; messages: ChatMessage[] }> {
  const stored = storageService.get<unknown>(THREAD_CONVERSATIONS_KEY, {});
  if (!isConversationMap(stored)) return [];
  return Object.entries(stored)
    .map(([itemId, messages]) => ({
      itemId,
      messages: Array.isArray(messages) ? (messages as ChatMessage[]) : [],
    }))
    .filter((entry) => entry.messages.length > 0);
}

export function clearThreadChatHistories(): void {
  storageService.removeItem(THREAD_CONVERSATIONS_KEY);
}

export function removeThreadChatHistory(itemId: string): void {
  const stored = storageService.get<unknown>(THREAD_CONVERSATIONS_KEY, {});
  if (!isConversationMap(stored)) return;
  const next: ThreadConversationMap = {};
  for (const key of Object.keys(stored)) {
    if (key !== itemId) next[key] = stored[key];
  }
  storageService.set(THREAD_CONVERSATIONS_KEY, next);
}

export function isChatMigrationDone(): boolean {
  return storageService.get<boolean>(CHAT_MIGRATION_KEY, false) === true;
}

export function markChatMigrationDone(): void {
  storageService.set(CHAT_MIGRATION_KEY, true);
}

export function getRadarChatHistory(): ChatMessage[] {
  const history = storageService.get<unknown>(RADAR_CHAT_KEY, []);
  return Array.isArray(history) ? history : [];
}

export function saveRadarChatHistory(messages: ChatMessage[]): void {
  storageService.set(RADAR_CHAT_KEY, pruneChatHistory(messages));
}